import AbaSaques from '@/assets/icons/AbaSaques';
import Badge from '@/components/shared/Badge/Badge';
import { Card } from '@/components/ui/card';
import { TableCell, TableRow } from '@/components/ui/table';

import { ExportButton } from '@/components/shared/ExportButton';
import { Input } from '@/components/shared/Input/Input';
import TableListCustom from '@/components/shared/TableListCustom/TableListCustom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TransactionResponse } from '@/services/wallet.service';
import { HelpCircle, Search } from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CTATable from '@/components/shared/TableListCustom/CTATable';
import DateRangePicker from '@/pages/Dashboard/Partials/DateRangePicker';
import { DateRange } from 'react-day-picker';
import { formatDateForAPI, getDefaultDate, isValidDate } from '@/utils/dateHelpers';

interface StatementListProps {
  transactions?: TransactionResponse;
  isLoading: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
  query: string;
  onQueryChange: (value: string) => void | Promise<URLSearchParams>;
  status: string;
  onStatusChange: (value: string) => void | Promise<URLSearchParams>;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange | undefined) => void;
  currency: string;
}

export default function StatementList({
  transactions,
  isLoading,
  currentPage,
  onPageChange,
  query,
  onQueryChange,
  status,
  onStatusChange,
  dateRange,
  onDateRangeChange,
  currency,
}: StatementListProps) {
  const { t } = useTranslation(['wallet', 'sales']);
  const [localQuery, setLocalQuery] = useState(query);
  const transactionsData = transactions ?? { data: [] };

  useEffect(() => {
    setLocalQuery(query);
  }, [query]);

  const exportStartDate = useMemo(() => {
    const fromDate = dateRange?.from || getDefaultDate();
    return isValidDate(fromDate) ? formatDateForAPI(fromDate) : formatDateForAPI(getDefaultDate());
  }, [dateRange?.from]);

  const exportEndDate = useMemo(() => {
    const toDate = dateRange?.to || getDefaultDate();
    return isValidDate(toDate) ? formatDateForAPI(toDate) : formatDateForAPI(getDefaultDate());
  }, [dateRange?.to]);

  const headers = [
    { title: t('date'), key: 'date' },
    { title: t('reference'), key: 'reference' },
    { title: t('type'), key: 'type' },
    { title: t('value'), key: 'value' },
  ];

  return (
    <Card>
      <div className='px-4 py-5 border-b border-b-colorBorder flex items-center justify-between'>
        <h3 className='text-base font-semibold'>{t('accountStatement')}</h3>
        <ExportButton
          type="financial"
          filters={{
            startDate: exportStartDate,
            endDate: exportEndDate,
            status: status !== 'all' ? status : undefined,
            q: query || undefined,
            currency: currency || undefined,
          }}
          label={t("sales:export")}
          title={t("sales:export_modal.title")}
          variant="secondary"
          size="sm"
        />
      </div>


      <div className='flex justify-between w-full items-center max-sm:flex-col px-4 py-5 gap-4'>
        <div className='grid grid-cols-3 max-md:grid-cols-1 gap-4 w-full'>
          <DateRangePicker
            defaultValue={dateRange}
            onChange={onDateRangeChange}
            widthFull
          />

          <Input
            className='w-full'
            icon={<Search size={15} />}
            placeholder={t('common:search_sales_placeholder')}
            value={localQuery}
            onChange={(e) => {
              setLocalQuery(e.target.value);
              onPageChange(1);
              onQueryChange(e.target.value);
            }}
          />

          <Select
            value={status}
            onValueChange={(value) => {
              onPageChange(1);
              onStatusChange(value);
            }}
          >
            <SelectTrigger className='w-full'>
              <SelectValue placeholder={t('common:filterAll')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>{t('common:filterAll')}</SelectItem>
              <SelectItem value='credited'>{t('common:credited')}</SelectItem>
              <SelectItem value='retention_released'>{t('common:retention_released')}</SelectItem>
              <SelectItem value='chargeback'>{t('common:chargeback')}</SelectItem>
              <SelectItem value='chargeback_fee'>{t('common:chargeback_fee')}</SelectItem>
              <SelectItem value='refunded'>{t('common:refunded')}</SelectItem>
              <SelectItem value='withdrawal'>{t('common:withdrawal')}</SelectItem>
              <SelectItem value='withdrawal_fee'>{t('common:withdrawal_fee')}</SelectItem>
              <SelectItem value='fee'>{t('common:fee')}</SelectItem>
              <SelectItem value='adjustment'>{t('common:adjustment')}</SelectItem>
              <SelectItem value='anticipation'>{t('common:anticipation')}</SelectItem>
              <SelectItem value='withdrawal_return'>{t('common:withdrawal_return')}</SelectItem>
              <SelectItem value='withdrawal_return_fee'>{t('common:withdrawal_return_fee')}</SelectItem>
              <SelectItem value='forced_retention'>{t('common:forced_retention')}</SelectItem>
              <SelectItem value='forced_retention_released'>{t('common:forced_retention_released')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <TableListCustom
        emptyDescriptionMessage={t('movementsDescription')}
        emptyIconMessage={<AbaSaques />}
        emptyTitleMessage={t('noMovements')}
        headers={headers}
        isLoadingData={isLoading}
        data={transactionsData}
        currentPage={currentPage}
        setCurrentPage={onPageChange}
      >
        {transactionsData?.data?.map((transaction) => (
            <TableRow
              key={transaction.id}
              className={`border-t border-zinc-800 text-muted-foreground whitespace-nowrap`}
            >
              <TableCell>{transaction.created_at_formatted}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <span>{transaction.reference_uuid}</span>
                  {
                    transaction?.description
                    && transaction.description !== "N/D"
                    && transaction.description.trim() !== ""
                    && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{transaction.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Badge status={transaction.type.toLowerCase() as any} />
              </TableCell>
              <TableCell
                className={`py-4 ${
                transaction.operation.toLocaleLowerCase() == 'cash_in'
                  ? 'text-primary'
                  : 'text-white'
                }`}
              >
                {transaction.amount_formatted}
              </TableCell>
            </TableRow>
        ))}
      </TableListCustom>
    </Card>
  );
}
